import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  DateInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  Toolbar,
  SaveButton,
  useTranslate,
} from 'react-admin';
import { Dialog, DialogTitle, DialogContent, IconButton, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ServiceProviderField } from '../ServiceProviderField';

const MaintenanceToolbar = () => (
  <Toolbar
    sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '16px 24px',
      borderTop: '1px solid #e0e0e0',
    }}
  >
    <SaveButton />
  </Toolbar>
);

interface MaintenanceDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: number;
  recordId?: number;
  isEdit?: boolean;
}

export const MaintenanceDialog: React.FC<MaintenanceDialogProps> = ({
  open,
  onClose,
  equipmentId,
  recordId,
  isEdit = false,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [record, setRecord] = useState(null);

  const serviceTypes = [
    { id: 'OIL_CHANGE', name: 'Oil Change' },
    { id: 'INSPECTION', name: 'Inspection' },
    { id: 'REPAIR', name: 'Repair' },
    { id: 'TIRE_SERVICE', name: 'Tire Service' },
    { id: 'BRAKE_SERVICE', name: 'Brake Service' },
    { id: 'ENGINE_SERVICE', name: 'Engine Service' },
    { id: 'TRANSMISSION_SERVICE', name: 'Transmission Service' },
    { id: 'ELECTRICAL', name: 'Electrical' },
    { id: 'BODYWORK', name: 'Bodywork' },
    { id: 'PREVENTIVE', name: 'Preventive Maintenance' },
    { id: 'EMERGENCY', name: 'Emergency Repair' },
    { id: 'OTHER', name: 'Other' },
  ];

  // Load record for edit mode
  useEffect(() => {
    if (isEdit && recordId && open) {
      true;
      dataProvider
        .getOne('equipment-maintenance', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
        })
        .catch(error => {
          console.error('Error loading maintenance record:', error);
          notify('Error loading maintenance record', { type: 'error' });
        })
        .finally(() => {
          false;
        });
    } else if (!isEdit) {
      setRecord(null);
    }
  }, [isEdit, recordId, open, dataProvider, notify]);

  const handleSubmit = async (data: any) => {
    true;
    try {
      const formattedData = {
        ...data,
        equipmentId: Number(equipmentId),
      };

      if (isEdit && recordId) {
        await dataProvider.update('equipment-maintenance', {
          id: recordId,
          data: formattedData,
        });
        notify('Maintenance record updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('equipment-maintenance', {
          data: formattedData,
        });
        notify('Maintenance record created successfully', { type: 'success' });
      }

      refresh();
      onClose();
    } catch (error) {
      console.error('Maintenance submission error:', error);
      notify(
        `Error ${isEdit ? 'updating' : 'creating'} maintenance record: ${
          error.message || 'Unknown error'
        }`,
        { type: 'error' }
      );
    } finally {
      false;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEdit
          ? translate('resources.equipment_maintenance.edit.title')
          : translate('resources.equipment_maintenance.create.title')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SimpleForm
          onSubmit={handleSubmit}
          record={
            record || {
              serviceDate: new Date().toISOString().split('T')[0],
            }
          }
          toolbar={<MaintenanceToolbar />}
        >
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Service Provider Section - Top Priority */}
            <Grid item xs={12}>
              <ServiceProviderField source="serviceProviderId" label="Service Provider" />
            </Grid>

            {/* Service Details */}
            <Grid item xs={12} md={6}>
              <DateInput
                source="serviceDate"
                validate={[required()]}
                fullWidth
                label="Service Date"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="serviceType"
                choices={serviceTypes}
                validate={[required()]}
                fullWidth
                label="Service Type"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput source="serviceMileage" fullWidth label="Service Mileage" />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput source="totalCost" fullWidth label="Total Cost ($)" />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source="description"
                fullWidth
                multiline
                rows={3}
                label="Service Description"
              />
            </Grid>
          </Grid>
        </SimpleForm>
      </DialogContent>
    </Dialog>
  );
};
